import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import MaterialDialogContent from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/DialogContent';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import { currency } from 'Helpers/Formats';
import Button from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Button';
import { fullGeneralDateWithDay } from 'Consts/DateFormats';
import { getApexACHRelationshipTransferStatusColor } from 'Consts/Apex';
import fr from 'facet-rest';
import Select from 'Components/ComponentLibrary/Atoms/Inputs/Select';

const styles = theme => ({
  root: {
    padding: theme.spacing(4, 3),
  },
  transferAmountContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: theme.spacing(4),
  },
  row: {
    borderTop: `1px solid ${theme.palette.grey[200]}`,
    display: 'flex',
    paddingTop: theme.spacing(2),
    justifyContent: 'space-between',
  },
  transferAmount: {
    fontWeight: theme.typography.fontWeightBold,
  },
  transferDetail: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 0),
    borderTop: `1px solid ${theme.palette.grey[200]}`,
  },
  actionButtons: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
  },
  isErrorState: {
    color: theme.palette.facet.red,
  },
  isSuccessState: {
    color: theme.palette.facet.accentGreen,
  },
  transferDetailValue: {
    maxWidth: '50%',
  },
  updateButtonPadding: {
    marginTop: theme.spacing(0.5),
  },
});

const AccountTransferDialogContent = ({
  className,
  classes,
  accountTransferResource,
  accountName,
  cancelTransfer,
  updateTransfer,
}) => {
  const canCancel = accountTransferResource?.relationship('cancelTransfer')?.link('related');

  const [iraContributionAttribute, setIraContributionAttribute] = useState(accountTransferResource.attr('iraContributionApplyToPriorYear'));

  const canUpdateContributionYear = accountTransferResource?.relationship(fr.relationships.update.name);

  const statusPrompt = accountTransferResource
    .meta()
    .field('status')
    .selectedOption()
    ?.attr('prompt');

  const statusColors = getApexACHRelationshipTransferStatusColor(accountTransferResource.attr('status'));

  // eslint-disable-next-line
    const TransferDetail = ({title, value}) => {
    return (
      <div className={classes.transferDetail}>
        <Typography
          variant="h6"
        >
          {title}
        </Typography>
        <Typography
          variant="body"
          className={classes.transferDetailValue}
        >
          {value}
        </Typography>
      </div>
    );
  };

  return (
    <MaterialDialogContent
      className={classNames(classes.root, className)}
      disableTypography
    >
      <div
        className={classNames(classes.transferAmountContainer)}
        style={{
          color: statusColors.iconColor,
        }}
      >
        <Typography
          className={classes.transferAmount}
          variant="h1"
        >
          {accountTransferResource.attr('fullLiquidation') === 'true' ? 'Total Balance' : currency(accountTransferResource.attr('amount'))}
        </Typography>
        <Typography
          variant="h6"
        >
          {statusPrompt}
        </Typography>
      </div>
      <div>
        <TransferDetail
          title="Transfer Amount"
          value={accountTransferResource.attr('fullLiquidation') === 'true' ? 'Total Balance' : currency(accountTransferResource.attr('amount'))}
        />
        { accountName !== ''
            && (<TransferDetail
              title="Account"
              value={accountName}
            />)
        }
        <TransferDetail
          title="Date Initiated"
          value={moment(accountTransferResource.attr('transferDate')).format(fullGeneralDateWithDay)}
        />
        <TransferDetail
          title="Status Details"
          value={accountTransferResource.attr('statusDetails')}
        />
        {
          accountTransferResource.attr('federalTaxWithholdingPercentage')
            ? (<TransferDetail
              title={accountTransferResource.meta().field('federalTaxWithholdingPercentage').attr('prompt')}
              value={accountTransferResource.attr('federalTaxWithholdingPercentage')}
            />) : null
        }
        {
          accountTransferResource.attr('stateTaxWithholdingPercentage')
            ? (<TransferDetail
              title={accountTransferResource.meta().field('stateTaxWithholdingPercentage').attr('prompt')}
              value={accountTransferResource.attr('stateTaxWithholdingPercentage')}
            />) : null
        }
        {
          accountTransferResource.attr('iraContributionType')
            ? (<TransferDetail
              title={accountTransferResource.meta().field('iraContributionType').attr('prompt')}
              value={accountTransferResource.selectedOptionPrompt('iraContributionType')}
            />) : null
        }
        {
          accountTransferResource.attr('iraDistributionReason')
            ? (<TransferDetail
              title={accountTransferResource.meta().field('iraDistributionReason').attr('prompt')}
              value={accountTransferResource.selectedOptionPrompt('iraDistributionReason')}
            />) : null
        }
        {
          !canUpdateContributionYear && accountTransferResource.attr('contributionYear') ? (
            <TransferDetail
              title={accountTransferResource.meta().field('contributionYear').attr('prompt')}
              value={accountTransferResource.attr('contributionYear')}
            />
          ) : null
        }
        { canUpdateContributionYear && accountTransferResource.attr('iraContributionApplyToPriorYear')
          ? (
            <div className={classes.row}>
              <Typography
                variant="h6"
              >
                {accountTransferResource.meta().field('iraContributionApplyToPriorYear').attr('prompt')}
              </Typography>
              <Select
                color="white"
                onChange={(event) => {
                  setIraContributionAttribute(event.value);
                }
                      }
                options={accountTransferResource.meta().field('iraContributionApplyToPriorYear').options().list()
                  .map(option => ({
                    name: option.attr('prompt'),
                    value: option.attr('id'),
                  }))}
                padding="dense"
                size="s"
                value={iraContributionAttribute}
                variant="contained"
                weight="bold"
              />
            </div>)
          : null }


      </div>
      {canCancel && (
        <div className={classes.actionButtons}>
          <Button
            color="red"
            onClick={cancelTransfer}
          >
            {'Cancel Transfer'}
          </Button>
        </div>)
            }
      { canUpdateContributionYear
        ? (
          <div className={classNames(classes.actionButtons, classes.updateButtonPadding)}>
            <Button
              color="primary"
              onClick={() => {
                updateTransfer(iraContributionAttribute);
              }}
            >
              {'Update Transfer'}
            </Button>
          </div>) : null
      }
    </MaterialDialogContent>
  );
};

AccountTransferDialogContent.defaultProps = {
  className: '',
  accountName: '',
};

AccountTransferDialogContent.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  accountTransferResource: PropTypes.object.isRequired,
  accountName: PropTypes.string,
  cancelTransfer: PropTypes.func.isRequired,
  updateTransfer: PropTypes.func.isRequired,
};

export default withStyles(styles)(AccountTransferDialogContent);
